import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { history } from '@umijs/max';
import { getCallQueueAssignments, onCallbackQueueUpdated } from '@/services/app/tenant/call-queue';
import { ensureNotificationSocketConnected } from '@/services/app/websocket';
import { LS_TOKEN_NAME } from '@/constants';
import { isPublicRoute } from '@/util';

export interface QueueItem {
  id:             number;        // our backend row id
  external_id:    string | null; // Appointment Center entry id (for their API)
  source:         string | null; // origin: 'appt_center' | 'dispatch_butler'
  customer_phone: string;
  customer_name:  string | null;
  reason:         string | null;
  attempts:       number;
  status:         'pending' | 'in_progress' | 'called_back' | 'no_answer' | 'resolved' | 'dismissed';
  assigned_to:    number | null;
  is_private:     number | boolean | null; // 1 = private to assigned_to (wrap-up "callback" outcome); never on the shared board
  assigned_to_user?: { id: number; user_name: string | null } | null; // owner, eager-loaded for the Team tab
  due_at:         string | null; // when the callback is due (wrap-up follow-ups)
  reminder_sent_at?: string | null; // when the due-time bell reminder went to the owner
  dispatch_id:    number | null; // job the callback belongs to, when known
  notes:          string | null;
  created_at:     string;
  called_back_at: string | null;
  dismissed_at:   string | null;
  contacted_at:   string | null; // latest outbound call to the number after this callback was created
  contacted_by:   string | null; // CSR who made that outbound call
  contacted_from: string | null; // number the outbound call was placed from
}

export type StreamStatus = 'connecting' | 'connected' | 'disconnected';

interface CallbackQueueContextValue {
  queue:            QueueItem[];  // top pending only — the nav badge popover preview (shared board)
  pendingCount:     number;       // accurate total pending on the shared board (backend COUNT, not the preview length)
  minePendingCount: number;       // the current user's own private callbacks still pending (wrap-up "callback" outcomes)
  streamStatus:     StreamStatus;
  reconnect:        () => void;
}

const CallbackQueueContext = createContext<CallbackQueueContextValue>({
  queue:            [],
  pendingCount:     0,
  minePendingCount: 0,
  streamStatus:     'connecting',
  reconnect:        () => {},
});

export const useCallbackQueue = () => useContext(CallbackQueueContext);

// This provider is app-wide, so it stays light: only the oldest-waiting pending items (the
// most urgent, for the badge popover) plus the exact pending count. The full, paginated
// queue lives in the Call Board panel, which fetches its own pages on demand.
// A "call-queue-updated" WS push (see onCallbackQueueUpdated below) applies state straight
// from the payload the instant a callback is created or changes status — safe here because
// the callback board is shared/tenant-wide, not per-user, so every connected user is meant
// to see the same list. No poll interval: fetchQueue only runs on mount, on a push, and on
// a socket reconnect (a dropped push during a disconnect never arrives on its own — see
// onSocketReconnected in call-queue.ts). Deliberately NOT re-fetching on visibilitychange —
// an open WebSocket stays connected while a tab is merely backgrounded in every modern
// browser, so that was just firing on ordinary window-switching with no real signal behind it.
const BADGE_LIMIT = 15;

// This provider lives in rootContainer, above the auth boundary, so it survives logout
// (an SPA navigation) and also mounts on the login page. Same threshold as the request
// interceptor, which only attaches tokens longer than 20 chars — anything shorter would
// poll unauthenticated and 401 forever.
const hasAuthToken = () => (localStorage.getItem(LS_TOKEN_NAME) || '').length > 20;

export const CallbackQueueProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [queue,        setQueue]        = useState<QueueItem[]>([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [minePendingCount, setMinePendingCount] = useState(0);
  const [streamStatus, setStreamStatus] = useState<StreamStatus>('connecting');

  const unmountedRef = useRef(false);

  const fetchQueue = useCallback(async () => {
    // This provider lives above the router (rootContainer), so it also mounts on public,
    // token-in-URL pages like /public-tracking. Those are tenant-unrelated and must never
    // trigger a call-queue fetch — a stale/invalid localStorage token there would 401 and,
    // absent this guard, still be harmless now that the global error handler also skips
    // public routes, but there's no reason to poll a tenant-only endpoint from a public page.
    if (isPublicRoute(window.location.pathname) || !hasAuthToken()) {
      if (!unmountedRef.current) {
        setQueue([]);
        setPendingCount(0);
        setMinePendingCount(0);
        setStreamStatus('disconnected');
      }
      return;
    }
    try {
      const res: any = await getCallQueueAssignments({ status: 'pending', limit: BADGE_LIMIT, sort: 'oldest' });
      if (unmountedRef.current) return;
      setQueue(Array.isArray(res?.assignments) ? res.assignments : []);
      setPendingCount(typeof res?.pending_count === 'number' ? res.pending_count : 0);
      // Only the REST response carries the caller's private count — a WS push is the
      // shared snapshot and leaves it as is (private rows never broadcast; the wrap-up
      // save and the Call Board fire a signal-only push to land here and refresh it).
      setMinePendingCount(typeof res?.mine_pending_count === 'number' ? res.mine_pending_count : 0);
      setStreamStatus('connected');
    } catch {
      if (!unmountedRef.current) setStreamStatus('disconnected'); // keep last known state
    }
  }, []);

  const reconnect = useCallback(() => { fetchQueue(); }, [fetchQueue]);

  useEffect(() => {
    unmountedRef.current = false;
    fetchQueue();
    ensureNotificationSocketConnected();
    // Applies a push straight from its payload — same shape/limit as fetchQueue's REST
    // response — instead of turning around and re-fetching. If WS_URL isn't configured,
    // or the socket never connects, this handler just never fires; the mount-time
    // fetchQueue() above is still the initial state either way.
    const unsubscribeWs = onCallbackQueueUpdated((data) => {
      if (unmountedRef.current) return;
      if (isPublicRoute(window.location.pathname) || !hasAuthToken()) return;
      if (!data) {
        // Signal-only push — a socket reconnect, or an older server with no
        // payload — re-fetch instead of guessing at what changed.
        fetchQueue();
        return;
      }
      setQueue(Array.isArray(data.assignments) ? data.assignments : []);
      setPendingCount(typeof data.pending_count === 'number' ? data.pending_count : 0);
      setStreamStatus('connected');
    });
    // Login/logout are SPA navigations that never unmount this provider, so watch for the
    // auth token flipping across route changes: populate right after login, and clear
    // stale state right after logout.
    const hadTokenRef = { current: hasAuthToken() };
    const unlisten = history.listen(() => {
      const has = hasAuthToken();
      if (has !== hadTokenRef.current) {
        hadTokenRef.current = has;
        fetchQueue();
      }
    });
    return () => {
      unmountedRef.current = true;
      unsubscribeWs();
      unlisten();
    };
  }, [fetchQueue]);

  return (
    <CallbackQueueContext.Provider value={{ queue, pendingCount, minePendingCount, streamStatus, reconnect }}>
      {children}
    </CallbackQueueContext.Provider>
  );
};
